/**
 * Tickets — internal service-desk list + create dialog.
 * Staff sees all tickets; regular users see only their own.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import MasterSelect from "@/components/MasterSelect";
import { Plus, Loader2, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";

const PRIORITY_STYLES = {
  urgent: "bg-red-500/15 text-red-500 border-red-500/30",
  high: "bg-orange-500/15 text-orange-500 border-orange-500/30",
  medium: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  low: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
};

const STATUS_STYLES = {
  open: "bg-blue-500/15 text-blue-500 border-blue-500/30",
  in_progress: "bg-primary/15 text-primary border-primary/30",
  pending: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  resolved: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  closed: "bg-muted text-muted-foreground border-border",
  reopened: "bg-fuchsia-500/15 text-fuchsia-500 border-fuchsia-500/30",
};

function timeLeft(sla) {
  if (!sla) return null;
  const diff = new Date(sla).getTime() - Date.now();
  if (diff < 0) return `Overdue by ${Math.abs(Math.round(diff / 3600000))}h`;
  const hours = Math.round(diff / 3600000);
  return hours < 1 ? `<1h left` : `${hours}h left`;
}

export default function Tickets() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [breachedOnly, setBreachedOnly] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    subject: "",
    description: "",
    department: "",
    priority: "medium",
    assigned_to: "",
  });
  const [teammates, setTeammates] = useState([]);
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (priorityFilter !== "all") params.priority = priorityFilter;
    if (breachedOnly) params.breached = true;
    try {
      const [{ data: list }, { data: s }] = await Promise.all([
        api.get("/crm/tickets", { params }),
        api.get("/crm/tickets/stats").catch(() => ({ data: null })),
      ]);
      setRows(list);
      setStats(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, priorityFilter, breachedOnly]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/crm/teammates").then((r) => setTeammates(r.data || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.description.trim()) {
      toast.error("Subject and description are required");
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.assigned_to) delete payload.assigned_to;
      await api.post("/crm/tickets", payload);
      toast.success("Ticket raised");
      setOpenDialog(false);
      setForm({ subject: "", description: "", department: "", priority: "medium", assigned_to: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to raise ticket");
    }
  };

  return (
    <div className="space-y-5" data-testid="tickets-page">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Stat label="Total" value={stats.total} testId="tickets-stat-total" />
          <Stat label="Open" value={stats.open} testId="tickets-stat-open" />
          <Stat label="Urgent" value={stats.by_priority?.urgent || 0} testId="tickets-stat-urgent" tone="destructive" />
          <Stat label="SLA breached" value={stats.sla_breached} testId="tickets-stat-breached" tone={stats.sla_breached > 0 ? "destructive" : "default"} />
          <Stat label="Resolved" value={stats.by_status?.resolved || 0} testId="tickets-stat-resolved" />
        </div>
      )}

      <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between">
        <div className="flex flex-wrap gap-2 items-center">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="tickets-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In progress</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="closed">Closed</SelectItem>
              <SelectItem value="reopened">Reopened</SelectItem>
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-[150px]" data-testid="tickets-filter-priority"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={breachedOnly ? "default" : "outline"}
            size="sm"
            onClick={() => setBreachedOnly((v) => !v)}
            data-testid="tickets-filter-breached"
          >
            <AlertTriangle className="w-3.5 h-3.5 mr-1.5" /> Breached only
          </Button>
        </div>

        <Dialog open={openDialog} onOpenChange={setOpenDialog}>
          <DialogTrigger asChild>
            <Button data-testid="ticket-create-btn"><Plus className="w-4 h-4 mr-1.5" /> Raise ticket</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display font-light text-2xl">Raise a ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label className="text-xs">Subject</Label>
                <Input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Short summary" data-testid="ticket-subject" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What happened? What did you expect?" data-testid="ticket-description" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Department</Label>
                  <MasterSelect
                    entityType="department"
                    value={form.department}
                    onChange={(v) => setForm({ ...form, department: v })}
                    placeholder="Choose department"
                    testId="ticket-department"
                  />
                </div>
                <div>
                  <Label className="text-xs">Priority</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger data-testid="ticket-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="urgent">Urgent (4h SLA)</SelectItem>
                      <SelectItem value="high">High (12h SLA)</SelectItem>
                      <SelectItem value="medium">Medium (24h SLA)</SelectItem>
                      <SelectItem value="low">Low (48h SLA)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Assign to (optional)</Label>
                <Select value={form.assigned_to || "unassigned"} onValueChange={(v) => setForm({ ...form, assigned_to: v === "unassigned" ? "" : v })}>
                  <SelectTrigger data-testid="ticket-assignee"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned queue</SelectItem>
                    {teammates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} · {t.role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="ticket-submit">Raise ticket</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-lg" data-testid="tickets-empty">
          No tickets yet. Click "Raise ticket" to create one.
        </div>
      ) : (
        <div className="grid gap-2">
          {rows.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => nav(`/app/crm/tickets/${t.id}`)}
              className="w-full text-left border border-border hover:border-primary/50 rounded-lg p-4 transition-colors bg-card"
              data-testid={`ticket-row-${t.ticket_number}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-muted-foreground">{t.ticket_number}</span>
                    <Badge variant="outline" className={PRIORITY_STYLES[t.priority] || ""}>{t.priority}</Badge>
                    <Badge variant="outline" className={STATUS_STYLES[t.status] || ""}>{t.status.replace("_", " ")}</Badge>
                    {t.department && <span className="text-xs text-muted-foreground">· {t.department}</span>}
                    {t.sla_breached && <Badge variant="outline" className="bg-red-500/15 text-red-500 border-red-500/30"><AlertTriangle className="w-3 h-3 mr-1" /> SLA breached</Badge>}
                  </div>
                  <div className="mt-1.5 font-medium truncate">{t.subject}</div>
                  <div className="text-sm text-muted-foreground line-clamp-1 mt-0.5">{t.description}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> {timeLeft(t.sla_due_at)}</div>
                  <div className="text-xs text-muted-foreground mt-1">{t.requester_name || "—"}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testId, tone }) {
  return (
    <div className="border border-border rounded-lg p-3" data-testid={testId}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-2xl font-display font-light mt-1 ${tone === "destructive" ? "text-red-500" : ""}`}>{value ?? 0}</div>
    </div>
  );
}
